import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useCallback, useState } from "react";
import Modal from "../modals/modal/Modal";
import styles from "./filterButton.module.css";

interface SpecificQuestionFilterModalProps {
  questionsAndAnswers: Map<string, string[]>;
  initialFilters: { question: string; answers: string[] }[];
  onApply: (filters: { question: string; answers: string[] }[]) => void;
  onClose: () => void;
}

const SpecificQuestionFilterModal: React.FC<SpecificQuestionFilterModalProps> = ({
  questionsAndAnswers,
  initialFilters,
  onApply,
  onClose,
}) => {
  const [localFilters, setLocalFilters] = useState<{ question: string; answers: string[] }[]>(initialFilters);

  const onToggleAnswer = useCallback((question: string, answer: string) => {
    setLocalFilters((prev) => {
      const existing = prev.find((f) => f.question === question);
      if (existing) {
        const hasAnswer = existing.answers.includes(answer);
        const newAnswers = hasAnswer
          ? existing.answers.filter((a) => a !== answer)
          : [...existing.answers, answer];
        if (newAnswers.length === 0) {
          return prev.filter((f) => f.question !== question);
        }
        return prev.map((f) =>
          f.question === question ? { ...f, answers: newAnswers } : f
        );
      }
      return [...prev, { question, answers: [answer] }];
    });
  }, []);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters([]);
  };

  return (
    <Modal onClose={onClose} prevencherAnswer>
      <div className={styles.specificModal}>
        <h3 className={styles.specificModalTitle}>{t("FILTER_OPTION_SPECIFIC_QUESTION")}</h3>
        <div className={styles.specificModalContent}>
          {Array.from(questionsAndAnswers.entries()).map(([question, answers]) => (
            <div key={question} className={styles.specificQuestionGroup}>
              <div className={styles.specificQuestionTitle}>{question}</div>
              <div className={styles.specificAnswersList}>
                {answers.map((answer) => {
                  const isActive = localFilters.some(
                    (sf) => sf.question === question && sf.answers.includes(answer)
                  );
                  return (
                    <div
                      key={answer}
                      className={`${styles.specificAnswerItem} ${isActive ? styles.specificAnswerItemActive : ""}`}
                      onClick={() => onToggleAnswer(question, answer)}
                    >
                      <input
                        type="checkbox"
                        checked={isActive}
                        readOnly
                        className={styles.specificCheckbox}
                      />
                      {answer}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.specificModalActions}>
          <button className={styles.specificModalBtnClear} onClick={handleClear}>
            {t("FILTER_MAIN_PAGE_CLEAR_LABEL")}
          </button>
          <button className={styles.specificModalBtnApply} onClick={handleApply}>
            {t("CONFIRM")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SpecificQuestionFilterModal;
